import { makeStyles } from "@material-ui/styles";

export default makeStyles(theme => ({
  tabs: {
    marginBottom: theme.spacing(2),
  },
  // "All times are local" note under the page title.
  tzNote: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  // Row above the tabs holding the "Show all predictions" toggle + Share button.
  // Reserve the toggle/button height even when empty (days without locked matches,
  // e.g. Today) so the tabs and match table stay put regardless of the active tab.
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: 38,
  },
  // Heading for the LIVE section on the Today tab.
  liveHeader: {
    display: "flex",
    alignItems: "center",
    color: "green",
    fontWeight: "bold",
    margin: theme.spacing(2, 0, 1),
  },
  sectionHeader: {
    margin: theme.spacing(2, 0, 1),
  },
  // The match list, rendered as a table so its columns line up. Sized to its content
  // and packed to the left, mirroring the leaderboards table.
  matchTable: {
    width: "auto",
    // The theme pins every MuiTableRow to height: 56; undo that so rows hug their
    // content.
    "& .MuiTableRow-root": {
      height: "auto",
    },
    "& td": {
      whiteSpace: "nowrap",
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
    },
    // The expanded predictions block sits in a tinted, indented cell so it reads as
    // belonging to (but distinct from) the match row above it.
    "& $expansionCell": {
      backgroundColor: theme.palette.action.hover,
      paddingLeft: theme.spacing(5),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.75),
    },
  },
  // Subtle highlight for live matches.
  liveRow: {
    backgroundColor: "rgba(0, 128, 0, 0.07)",
  },
  // A match row that owns an expansion below it: drop its divider so it groups with
  // its predictions (the expansion cell carries the divider to the next match).
  expandedRow: {
    "& td": {
      borderBottom: "none",
    },
  },
  kickoff: {
    color: theme.palette.text.secondary,
  },
  teams: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  teamFlag: {
    height: "1.5rem",
    margin: theme.spacing(0, 1),
  },
  league: {
    color: theme.palette.text.secondary,
  },
  score: {
    display: "flex",
    alignItems: "center",
  },
  liveData: {
    color: "green",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
  },
  blinkingGreenDot: {
    height: "0.6rem",
    marginLeft: "0.3rem",
  },
  warningIcon: {
    color: "#f44336",
    verticalAlign: "middle",
  },
  // Tighten the clickable warning icon so it stays inline with the view button.
  warningButton: {
    padding: 2,
  },
  // Keep the action icons (warning + view predictions) on one line.
  actionsCell: {
    whiteSpace: "nowrap",
  },
  // The full-width row holding a match's inline predictions when "Show all" is on.
  // (Styling lives under matchTable's "& $expansionCell" so it beats the generic td.)
  expansionCell: {},
  // Title row with the match name and (for admins) the edit pencil alongside it.
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  dialogPredictions: {
    paddingTop: theme.spacing(1),
  },
  // Very compact predictions table, reused by the dialog and the inline expansion.
  predictionsTable: {
    width: "auto",
    // Undo the theme's fixed 56px row height so rows are only as tall as their text.
    "& .MuiTableRow-root": {
      height: "auto",
    },
    "& th, & td": {
      textAlign: "left",
      whiteSpace: "nowrap",
      border: "none",
      fontSize: "0.8rem",
      lineHeight: 1.2,
      padding: theme.spacing(0, 1.5, 0, 1),
    },
    "& th": {
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },
  // Fix the switch thumb sitting off-centre (the theme nudges PrivateSwitchBase right)
  // and give the label a little breathing room from the switch.
  toggle: {
    "& .MuiSwitch-switchBase": {
      marginLeft: 0,
    },
    "& .MuiFormControlLabel-label": {
      marginLeft: theme.spacing(1),
    },
  },
  emptyDay: {
    margin: theme.spacing(4, 0),
    color: theme.palette.text.secondary,
  },

  // --- Mobile (phone) stacked cards ---
  mCard: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mLiveCard: {
    backgroundColor: "rgba(0, 128, 0, 0.07)",
  },
  mTime: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    paddingTop: 2,
    minWidth: "3.25rem",
  },
  mBody: {
    flex: 1,
    minWidth: 0,
  },
  mMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  mLeague: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mActions: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
  mExpansion: {
    marginTop: theme.spacing(0.5),
    overflowX: "auto",
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(0.5, 1),
  },
}));
